import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildStocktakingWsUrl } from '../api/stocktaking';
import {
  IncomingStocktakingWsEvent,
  OutgoingStocktakingWsEvent,
  StocktakingSessionItem,
  WebsocketCountEvent,
  WebsocketSnapshotEvent,
} from '../types/stocktaking';

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

interface UseStocktakingRealtimeOptions {
  sessionId: number | null;
  initialItems?: StocktakingSessionItem[];
  autoJoin?: boolean;
  restUpdater?: (itemId: string, quantity: number) => Promise<StocktakingSessionItem | null>;
}

interface PendingAction {
  id: string;
  action: OutgoingStocktakingWsEvent;
  createdAt: number;
}

export interface StocktakingItemState extends StocktakingSessionItem {
  duplicate?: boolean;
  pending?: boolean;
  conflict?: boolean;
}

interface LastCloseInfo { code?: number; reason?: string; wasClean?: boolean }

export interface UseStocktakingRealtimeResult {
  state: ConnectionState;
  items: StocktakingItemState[];
  send: (event: OutgoingStocktakingWsEvent) => void;
  queueCount: (itemId: string, quantityDelta: number, absoluteQuantity?: number) => void;
  refreshSnapshot: () => void;
  clearPending: (itemId: string) => void;
  lastClose?: LastCloseInfo;
  lastError?: string;
  lockItem: (itemId: string) => void;
  unlockItem: (itemId: string) => void;
  isLockedByMe: (itemId: string) => boolean;
}

const MAX_RETRIES = 5;
const RETRY_DELAY = 3000;

export function useStocktakingRealtime({ sessionId, initialItems = [], autoJoin = true, restUpdater }: UseStocktakingRealtimeOptions): UseStocktakingRealtimeResult {
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [items, setItems] = useState<StocktakingItemState[]>(initialItems);
  const socketRef = useRef<WebSocket | null>(null);
  const queueRef = useRef<PendingAction[]>([]);
  const retryRef = useRef(0);
  const sessionRef = useRef(sessionId);
  sessionRef.current = sessionId;
  const restUpdaterRef = useRef<typeof restUpdater>(restUpdater);
  restUpdaterRef.current = restUpdater;
  const lastCloseRef = useRef<LastCloseInfo | undefined>();
  const [lastError, setLastError] = useState<string | undefined>();
  const existingClientId = window.localStorage.getItem('stocktakingClientId');
  let initialClientId = existingClientId;
  if (!initialClientId) {
    initialClientId = 'c-' + Math.random().toString(36).slice(2, 10);
    window.localStorage.setItem('stocktakingClientId', initialClientId);
  }
  const clientIdRef = useRef<string>(initialClientId);

  // Map for quick lookup
  const itemsMapRef = useRef<Map<string, StocktakingItemState>>(new Map());
  const channelRef = useRef<BroadcastChannel | null>(null);

  const normalizeQuantity = (value: any): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const n = parseFloat(value.replace(',', '.'));
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  const applyItems = useCallback((list: StocktakingSessionItem[], merge = false) => {
    itemsMapRef.current = merge ? new Map(itemsMapRef.current) : new Map();
    for (const row of list) {
      const key = String(row.itemId);
      const prev = merge ? itemsMapRef.current.get(key) : undefined;
      const pending = prev?.pending ?? false;
      const duplicate = prev?.duplicate ?? false;
      const counted = normalizeQuantity((row as any).countedQuantity ?? (row as any).stockTakenQuantity);
      const stockTaken = normalizeQuantity((row as any).stockTakenQuantity ?? (row as any).countedQuantity);
      itemsMapRef.current.set(key, {
        ...prev,
        ...row,
        countedQuantity: counted,
        stockTakenQuantity: stockTaken,
        pending,
        duplicate,
        conflict: checkConflict(row),
      });
    }
    setItems(Array.from(itemsMapRef.current.values()));
  }, []);

  const applyItem = useCallback((item: Partial<StocktakingSessionItem> & { itemId: string }) => {
    const map = new Map(itemsMapRef.current);
    const existing = map.get(item.itemId);
    const counted = normalizeQuantity(item.countedQuantity ?? item.stockTakenQuantity ?? existing?.countedQuantity);
    const stockTaken = normalizeQuantity(item.stockTakenQuantity ?? item.countedQuantity ?? existing?.stockTakenQuantity);
    const merged: StocktakingItemState = {
      ...existing,
      ...item,
      countedQuantity: counted,
      stockTakenQuantity: stockTaken,
  // Preserve existing countedBy if new item doesn't have it
  countedBy: item.countedBy || existing?.countedBy,
  // Preserve existing stock location data if new item doesn't have it
  stockLocationCode: item.stockLocationCode || existing?.stockLocationCode,
  stockLocationId: item.stockLocationId || existing?.stockLocationId,
      pending: existing?.pending && !('pending' in item && item.pending === false),
    } as StocktakingItemState;
    merged.conflict = checkConflict(merged);
    map.set(item.itemId, merged);
    itemsMapRef.current = map;
    setItems(Array.from(map.values()));
  }, []);

  const processEvent = useCallback(
    (event: IncomingStocktakingWsEvent, source: 'ws' | 'broadcast' = 'ws') => {
      switch (event.event) {
        case 'snapshot': {
          const snapshot = (event as WebsocketSnapshotEvent).items || [];
          if (snapshot.length === 0) {
            // Do not wipe existing items if server returns empty snapshot; instead mark existing as not pending
            const map = new Map(itemsMapRef.current);
            for (const [key, value] of map.entries()) {
              map.set(key, { ...value, pending: false });
            }
            itemsMapRef.current = map;
            setItems(Array.from(map.values()));
          } else {
            // Merge with existing to preserve optimistic counts & duplicate flags
            applyItems(snapshot, true);
            // After merge, clear pending on merged items since snapshot represents server state
            const map = new Map(itemsMapRef.current);
            for (const [key, value] of map.entries()) {
              map.set(key, { ...value, pending: false });
            }
            itemsMapRef.current = map;
            setItems(Array.from(map.values()));
          }
          queueRef.current = [];
          break;
        }
        case 'item':
        case 'count': {
          const payload = event as WebsocketCountEvent;
          applyItem({ 
            itemId: String(payload.itemId), 
            countedQuantity: payload.countedQuantity, 
            lockedBy: payload.lockedBy, 
            countedBy: payload.countedBy,
            pending: false 
          });
          queueRef.current = queueRef.current.filter(action => action.id !== payload.itemId);
          break;
        }
        case 'lock':
        case 'unlock': {
          const payload = event as any;
          const { itemId, lockedBy } = payload;
          if (itemId) {
            applyItem({ itemId: String(itemId), lockedBy });
          }
          break;
        }
        case 'error': {
          console.warn('[Stocktaking WS] error', (event as any)?.message);
          break;
        }
        default: {
          console.debug('[Stocktaking WS] event', event);
        }
      }
      if (source === 'ws' && channelRef.current) {
        try {
          channelRef.current.postMessage({ sessionId: sessionRef.current, payload: event });
        } catch (err) {
          console.warn('[Stocktaking] broadcast failed', err);
        }
      }
    },
    [applyItem, applyItems]
  );

  const flushQueue = useCallback(() => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    const ws = socketRef.current;
    for (const pending of queueRef.current) {
      ws.send(JSON.stringify(pending.action));
    }
  }, []);

  const connect = useCallback(() => {
    const id = sessionRef.current;
    if (!id) return;

    const url = buildStocktakingWsUrl(id);
    setConnectionState('connecting');
    try {
      const ws = new WebSocket(url);
      socketRef.current = ws;

      ws.onopen = () => {
        setConnectionState('open');
        retryRef.current = 0;
        if (autoJoin) {
          const joinEvent: OutgoingStocktakingWsEvent = { event: 'join', sessionId: id };
          ws.send(JSON.stringify(joinEvent));
        }
        flushQueue();
      };

      ws.onmessage = event => {
        try {
          const payload = JSON.parse(event.data) as IncomingStocktakingWsEvent;
          processEvent(payload);
        } catch (error) {
          console.error('[Stocktaking WS] Failed to parse message', error, event.data);
        }
      };

      ws.onerror = () => {
        setConnectionState('error');
        setLastError('Websocket error');
      };

      ws.onclose = ev => {
        setConnectionState('closed');
        socketRef.current = null;
        lastCloseRef.current = { code: ev.code, reason: ev.reason, wasClean: ev.wasClean };
        if (retryRef.current < MAX_RETRIES) {
          retryRef.current += 1;
          setTimeout(connect, RETRY_DELAY * retryRef.current);
        }
      };
    } catch (error) {
      console.error('[Stocktaking WS] connect failed', error);
      setConnectionState('error');
    }
  }, [autoJoin, flushQueue, processEvent]);

  useEffect(() => {
    if (!sessionId) return undefined;
    const channelName = `stocktaking_session_${sessionId}`;
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(channelName);
      channelRef.current = channel;
      channel.onmessage = ev => {
        const { sessionId: incomingSessionId, payload } = ev.data || {};
        if (incomingSessionId === sessionId && payload?.event) {
          try {
            processEvent(payload as IncomingStocktakingWsEvent, 'broadcast');
          } catch (e) {
            console.error('[Stocktaking] failed to process broadcast event', e);
          }
        }
      };
    } catch (e) {
      console.warn('[Stocktaking] BroadcastChannel unavailable', e);
    }
    connect();
    return () => {
      socketRef.current?.close();
      socketRef.current = null;
      setConnectionState('closed');
      try {
        channel?.close();
      } catch {}
      channelRef.current = null;
    };
  }, [connect, sessionId, processEvent]);

  useEffect(() => {
    applyItems(initialItems, false);
  }, [initialItems, applyItems]);

  const send = useCallback((event: OutgoingStocktakingWsEvent) => {
    const ws = socketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(event));
    } else {
      queueRef.current.push({ id: 'generic-' + Date.now(), action: event, createdAt: Date.now() });
    }
  }, []);

  const queueCount = useCallback(
    (itemId: string, quantityDelta: number, absoluteQuantity?: number) => {
      const ws = socketRef.current;
      const wsOpen = !!ws && ws.readyState === WebSocket.OPEN;
      
      // Get current user name
      let countedBy = 'Okänd användare';
      try {
        const meRaw = window.localStorage.getItem('me');
        if (meRaw) {
          const me = JSON.parse(meRaw);
          countedBy = me?.name || me?.username || me?.email || 'Okänd användare';
        }
      } catch {
        // Keep default
      }
      
      const action: OutgoingStocktakingWsEvent = {
        event: 'count',
        itemId,
        quantityDelta,
        countedBy,
        // Provide stock location data when available so server/broadcast can propagate it
        stockLocationCode: (itemsMapRef.current.get(itemId) as any)?.stockLocationCode,
        stockLocationId: (itemsMapRef.current.get(itemId) as any)?.stockLocationId,
      } as WebsocketCountEvent;

      // Optimistic update
      const map = new Map(itemsMapRef.current);
      const existing = map.get(itemId);
      const baseQuantity = existing?.countedQuantity ?? existing?.stockTakenQuantity ?? 0;
      const nextQuantity = typeof absoluteQuantity === 'number' ? absoluteQuantity : baseQuantity + quantityDelta;
      const duplicate = existing ? true : false;
      const updated: StocktakingItemState = {
        ...(existing || ({ itemId } as Partial<StocktakingSessionItem>)),
        itemId,
        countedQuantity: nextQuantity,
        stockTakenQuantity: nextQuantity,
        countedBy,
        stockLocationCode: (existing as any)?.stockLocationCode,
        stockLocationId: (existing as any)?.stockLocationId,
        pending: wsOpen,
        duplicate,
      } as StocktakingItemState;
      updated.conflict = checkConflict(updated);
      map.set(itemId, updated);
      itemsMapRef.current = map;
      setItems(Array.from(map.values()));

      if (!wsOpen) {
        const updater = restUpdaterRef.current;
        if (updater) {
          updater(itemId, nextQuantity).then(result => {
            const map2 = new Map(itemsMapRef.current);
            const current = map2.get(itemId) ?? updated;
            const merged: StocktakingItemState = {
              ...current,
              ...(result || {}),
              countedQuantity: result?.countedQuantity ?? nextQuantity,
              stockTakenQuantity: result?.stockTakenQuantity ?? nextQuantity,
              pending: false,
              duplicate: current.duplicate,
            };
            merged.conflict = checkConflict(merged);
            map2.set(itemId, merged);
            itemsMapRef.current = map2;
            setItems(Array.from(map2.values()));
          }).catch(err => {
            console.error('[Stocktaking] REST update failed', err);
          });
        }
        return;
      }

      queueRef.current = queueRef.current.filter(entry => entry.id !== itemId);
      queueRef.current.push({ id: itemId, action, createdAt: Date.now() });
      flushQueue();
      // Broadcast local optimistic count so other tabs can reflect immediately (without waiting for server echo)
      if (channelRef.current) {
        try {
          channelRef.current.postMessage({ sessionId: sessionRef.current, payload: { event: 'count', itemId, countedQuantity: nextQuantity } });
        } catch (e) {
          console.warn('[Stocktaking] broadcast count failed', e);
        }
      }
    },
    [flushQueue]
  );

  const refreshSnapshot = useCallback(() => {
    send({ event: 'snapshot' });
  }, [send]);

  const clearPending = useCallback((itemId: string) => {
    queueRef.current = queueRef.current.filter(entry => entry.id !== itemId);
    const map = new Map(itemsMapRef.current);
    const existing = map.get(itemId);
    if (existing) {
      existing.pending = false;
      map.set(itemId, existing);
      itemsMapRef.current = map;
      setItems(Array.from(map.values()));
    }
  }, []);

  const lockItem = useCallback((itemId: string) => {
    const ws = socketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      const evt: OutgoingStocktakingWsEvent = { event: 'lock', itemId } as any;
      ws.send(JSON.stringify(evt));
      // Optimistic mark
      applyItem({ itemId, lockedBy: clientIdRef.current });
    }
  }, [applyItem]);

  const unlockItem = useCallback((itemId: string) => {
    const ws = socketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      const evt: OutgoingStocktakingWsEvent = { event: 'unlock', itemId } as any;
      ws.send(JSON.stringify(evt));
      applyItem({ itemId, lockedBy: null });
    }
  }, [applyItem]);

  const isLockedByMe = useCallback((itemId: string) => {
    const entry = itemsMapRef.current.get(itemId);
    return !!entry && entry.lockedBy === clientIdRef.current;
  }, []);

  return useMemo(() => ({
    state: connectionState,
    items,
    send,
    queueCount,
    refreshSnapshot,
    clearPending,
    lastClose: lastCloseRef.current,
    lastError,
    lockItem,
    unlockItem,
    isLockedByMe,
  }), [connectionState, items, queueCount, refreshSnapshot, send, clearPending, lastError, lockItem, unlockItem, isLockedByMe]);
}

function checkConflict(item: Partial<StocktakingSessionItem>): boolean {
  if (typeof item.stockTakenQuantity !== 'number') return false;
  if (typeof item.systemQuantity !== 'number') return false;
  return Math.round(item.stockTakenQuantity * 100) !== Math.round(item.systemQuantity * 100);
}
